import { useState } from 'react';
import * as R from 'remeda';
import { ControllerFieldState, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { SchemaSection } from '@rossum/api-client/schemas';
import { ContentWrapper } from '@rossum/components/content-group/ContentWrapper';
import { SplitContent } from '@rossum/components/content-group/SplitContent';
import { SuggesterIconButton } from '@rossum/components/variable-suggestions/SuggesterIconButton';
import { SuggestVariables } from '@rossum/components/variable-suggestions/SuggestVariables';
import {
  IconCode,
  IconCodeOff,
  IconCustomAurora,
} from '@rossum/ui/icons/tabler';
import {
  Button,
  Collapse,
  Divider,
  IconButton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { FormulaEditor } from '../FormulaEditor/FormulaEditor';
import { getValidSchemaFields } from '../FormulaEditor/helpers';
import { getFieldCompletions } from '../getFieldCompletions';
import {
  baseSuggesterSections,
  contextSuggesterSections,
} from '../suggesterSections';
import { FormulaCopilot } from './components/FormulaCopilot';
import { SummaryBox } from './components/SummaryBox';
import { handleInsertion } from './helpers/insertion-helpers';
import { updateFieldFormula } from './helpers/updateFieldFormula';
import { useFormulaSummary } from './hooks/useSuggestFormulaDescription';

const MIN_VISIBLE_EDITOR_LINES = 5;

type FormulaFieldSectionProps = {
  schemaContent: SchemaSection[];
  formulaSchemaId: string;
  onChange: () => void;
  disabled: boolean;
  fieldState: ControllerFieldState;
  field: {
    onBlur: () => void;
    onChange: (newValue: string) => void;
    value: string;
  };
};

export const FormulaFieldSection = ({
  schemaContent,
  formulaSchemaId,
  onChange,
  disabled,
  fieldState,
  field,
}: FormulaFieldSectionProps) => {
  const formula = field.value;
  const errorMessage =
    fieldState.invalid && fieldState.error ? fieldState.error.message : null;

  const formMethods = useFormContext();

  const [triggerCopilotOpen, setTriggerCopilotOpen] = useState(false);

  const [codeEditorVisible, setCodeEditorVisible] = useState(false);

  const createDraftSchema = (formulaDraft: string) =>
    formulaDraft
      ? {
          schemaContent: updateFieldFormula(
            schemaContent,
            formulaSchemaId,
            formulaDraft
          ),
          fieldSchemaId: formulaSchemaId,
        }
      : undefined;

  const [schemaForSummary, setSchemaForSummary] = useState(() =>
    createDraftSchema(formula)
  );

  const schemaForSummaryUpToDate = R.isDeepEqual(
    createDraftSchema(formula),
    schemaForSummary
  );

  const { data: summary, isFetching: isSuggestingSummary } =
    useFormulaSummary(schemaForSummary);

  const intl = useIntl();

  const baseSections = baseSuggesterSections(intl, {
    fields: getFieldCompletions(schemaContent),
  });

  return (
    <>
      <ContentWrapper>
        <SplitContent
          disabled={disabled}
          title={intl.formatMessage({
            id: 'features.formulas.formulaFieldSection.title',
          })}
          description={intl.formatMessage({
            id: 'features.formulas.formulaFieldSection.description',
          })}
        >
          {isSuggestingSummary ? (
            <SummaryBox variant="suggestingSummary" />
          ) : disabled ? (
            <SummaryBox variant="missingInfoFormula" />
          ) : !schemaForSummaryUpToDate ? (
            <SummaryBox
              variant="editingCode"
              callback={() => {
                if (!errorMessage) {
                  setSchemaForSummary(createDraftSchema(formula));
                }
              }}
            />
          ) : summary ? (
            <SummaryBox
              value={summary}
              onClick={() => setTriggerCopilotOpen(true)}
            />
          ) : (
            <SummaryBox
              variant="emptyFormula"
              callback={() => setTriggerCopilotOpen(true)}
            />
          )}
        </SplitContent>

        {disabled ? null : (
          <Stack>
            <Collapse in={codeEditorVisible}>
              <Stack spacing={2} pt={3}>
                <Divider />
                <Stack spacing={1}>
                  <FormulaEditor
                    placeholder={intl.formatMessage({
                      id: 'features.formulas.formulaFieldSection.formulaEditorPlaceholder',
                    })}
                    fields={getValidSchemaFields(schemaContent)}
                    value={formula}
                    onChange={newValue => {
                      onChange();
                      field.onChange(newValue);
                      // We need to validate syntax in formula editor on change in order to disable preview and apply code button
                      // Basically to avoid 400 requests to formulaInfo and evaluateFormulas
                      formMethods.trigger('field.formula');
                    }}
                    onBlur={field.onBlur}
                    config={{ minVisibleLines: MIN_VISIBLE_EDITOR_LINES }}
                    renderButtons={({ editor }) => (
                      <Stack direction="row" spacing={1}>
                        <SuggestVariables
                          sections={[
                            baseSections.fields,
                            baseSections.annotation,
                            baseSections.document,
                            baseSections.email,
                            ...contextSuggesterSections(intl),
                          ]}
                          mode="formula"
                          onInsert={(insertValue: string) => {
                            handleInsertion({
                              value: insertValue,
                              meta: { insertMode: 'inline' },
                              editor,
                            });

                            // need to use setTimeout because closing the menu
                            // steals focus from editor back to the button
                            setTimeout(() => {
                              editor.focus();
                            });
                          }}
                        >
                          <Tooltip
                            title={intl.formatMessage({
                              id: 'features.rules.copilotVariable',
                            })}
                            placement="top"
                          >
                            <span>
                              <SuggesterIconButton
                                dataCy="copilot-dialog-suggester-icon-button"
                                disabled={false}
                              />
                            </span>
                          </Tooltip>
                        </SuggestVariables>
                      </Stack>
                    )}
                  />
                  {errorMessage ? (
                    <Typography color="error" variant="body2">
                      {errorMessage}
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
            </Collapse>
          </Stack>
        )}

        <Stack direction="row" spacing={2} justifyContent="end" pt={2}>
          <Tooltip
            title={
              codeEditorVisible
                ? intl.formatMessage({
                    id: 'features.formulas.hideSourceCode',
                  })
                : intl.formatMessage({
                    id: 'features.formulas.showSourceCode',
                  })
            }
          >
            <IconButton
              disabled={disabled}
              color="inherit"
              data-cy={`formula-field-${codeEditorVisible ? 'hide' : 'show'}-code-btn`}
              onClick={() => setCodeEditorVisible(!codeEditorVisible)}
            >
              <SvgIcon fontSize="large">
                {codeEditorVisible ? <IconCodeOff /> : <IconCode />}
              </SvgIcon>
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="aurora"
            data-cy="formula-field-copilot-edit-btn"
            onClick={() => setTriggerCopilotOpen(true)}
            startIcon={
              <SvgIcon fontSize="large">
                <IconCustomAurora />
              </SvgIcon>
            }
            disabled={disabled}
          >
            {intl.formatMessage({
              id: 'features.formulas.copilotEdit',
            })}
          </Button>
        </Stack>
      </ContentWrapper>
      <FormulaCopilot
        key={triggerCopilotOpen.toString()}
        open={triggerCopilotOpen}
        onClose={() => setTriggerCopilotOpen(false)}
        onSuccess={response => {
          if (response?.formula) {
            onChange();
            field.onChange(response.formula);
            setSchemaForSummary(createDraftSchema(response.formula));
          }
          setTriggerCopilotOpen(false);
        }}
        schemaContent={
          // Avoids error: "Datapoints of type 'formula' need to have a formula defined"
          // Current schemaContent can have formula undefined, make sure to append the actual value before sending the request.
          updateFieldFormula(schemaContent, formulaSchemaId, formula)
        }
        fieldSchemaId={formulaSchemaId}
        isIterativeApproach={!!formula}
        summary={summary}
      />
    </>
  );
};
